import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // The server renders only the selected variant's media group — see the boundary filter in
    // blocks/_product-media-gallery.liquid — so the freshly rendered gallery is already correct
    // and needs no client-side filtering.
    //
    // Rebuild ONLY when the rendered media actually differ. replaceWith() recreates every <img>,
    // which visibly reloads the gallery; a size change within the same colour resolves to the
    // same group, so swapping there would reintroduce that flicker for no benefit. A colour
    // change (or a combined-listing product switch) yields a different set and does rebuild.
    if (getMediaSignature(newMediaGallery) === getMediaSignature(this)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    const sourceMedia = getEventMedia(event);
    const dialogMedia = this.refs.zoomDialogComponent?.refs?.media ?? [];
    const mediaIndex = getMediaIndex(dialogMedia, sourceMedia);

    this.refs.zoomDialogComponent?.open(mediaIndex >= 0 ? mediaIndex : index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Returns identifying data for the clicked/tapped gallery item.
 *
 * @param {PointerEvent} event - The pointer event.
 * @returns {{ mediaId: string | null, imageUrl: string | null }}
 */
function getEventMedia(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return { mediaId: null, imageUrl: null };

  const mediaContainer = target.closest('li, slideshow-slide, .product-media-container') ?? target;
  const mediaElement = target.closest('[data-media-id]') ?? mediaContainer.querySelector('[data-media-id]');
  const image = target.closest('img') ?? mediaContainer.querySelector('img');

  return {
    mediaId: mediaElement instanceof HTMLElement ? mediaElement.dataset.mediaId ?? null : null,
    imageUrl: getImageUrl(image),
  };
}

/**
 * Finds the current zoom-dialog media index for a clicked media signature.
 *
 * @param {HTMLElement[]} mediaItems - The zoom-dialog media refs.
 * @param {{ mediaId: string | null, imageUrl: string | null }} sourceMedia - The clicked media signature.
 * @returns {number}
 */
function getMediaIndex(mediaItems, sourceMedia) {
  if (sourceMedia.imageUrl) {
    const imageIndex = mediaItems.findIndex((item) => normalizeImageUrl(getImageUrl(item.querySelector('img'))) === sourceMedia.imageUrl);
    if (imageIndex >= 0) return imageIndex;
  }

  if (!sourceMedia.mediaId) return -1;

  return mediaItems.findIndex((item) => {
    if (item.dataset.mediaId === sourceMedia.mediaId) return true;

    const nestedMedia = item.querySelector('[data-media-id]');
    return nestedMedia instanceof HTMLElement && nestedMedia.dataset.mediaId === sourceMedia.mediaId;
  });
}

/**
 * Returns a normalized image URL for matching app-swapped gallery images.
 *
 * @param {Element | null} image - The image element.
 * @returns {string | null}
 */
function getImageUrl(image) {
  if (!(image instanceof HTMLImageElement)) return null;

  return normalizeImageUrl(
    image.getAttribute('data_max_resolution') ||
      image.currentSrc ||
      image.src ||
      image.getAttribute('srcset')?.split(',').pop()?.trim().split(/\s+/)[0] ||
      null
  );
}

/**
 * Normalizes Shopify image URLs so different width variants match the same source image.
 *
 * @param {string | null} src - The image URL.
 * @returns {string | null}
 */
function normalizeImageUrl(src) {
  if (!src) return null;

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.delete('width');
    return url.pathname + '?' + url.searchParams.toString();
  } catch (e) {
    return src.replace(/([?&])width=\d+(&)?/, '$1').replace(/[?&]$/, '');
  }
}

/**
 * Returns a stable signature of the media a gallery renders, in order.
 *
 * Used to decide whether a variant change actually changed the rendered media. Covers every
 * render path (carousel slides, desktop grid, zoom dialog) because each carries [data-media-id].
 *
 * @param {HTMLElement | null} mediaGallery - The media gallery element.
 * @returns {string}
 */
function getMediaSignature(mediaGallery) {
  if (!mediaGallery) return '';

  return Array.from(mediaGallery.querySelectorAll('[data-media-id]'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.mediaId ?? '' : ''))
    .filter(Boolean)
    .join(',');
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
